<script lang="ts">
    import type {SutorEmployeeDetail} from 'src/features/erp-sutor/pages/user-detail/types';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {getTextFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
    import {getInjector} from 'core/svelte-context/context';
    import {SutorUserDetailService} from 'src/features/erp-sutor/pages/user-detail/sutor-user-detail.service';
    import {exists} from 'shared/utils/custom-utils';
    import {FOND_EMPLOYMENT_STATUS, FOND_JOB_FILE} from 'src/features/erp-sutor/sutor-fonds';
    import LabeledValue from 'src/features/erp-sutor/pages/project-detail/reports-optimisation/overviews/components/LabeledValue.svelte';
    import KpHeading from 'shared/components/kp-heading/KpHeading.svelte';
    import GridFieldValue from 'shared/ui-widgets/grid/GridFieldValue.svelte';
    import Spacer from 'shared/layouts/flex/Spacer.svelte';
    import Flex from 'shared/layouts/flex/Flex.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import KpChipTag from 'shared/ui-widgets/chip/KpChipTag.svelte';
    import ErpFondPermissionContainer from 'src/features/erp/components/erp-fond-permission-container/ErpFondPermissionContainer.svelte';
    import TextNoteEditor from 'src/features/erp-sutor/pages/user-detail/TextNoteEditor.svelte';

    export let employeeDetail: SutorEmployeeDetail;

    const service = getInjector().getByClass(SutorUserDetailService);

    // Fond 83 (pracovní karta)
    const personCardRecord = employeeDetail.personCardRecord;
    const birthNumberField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.birthNumberFieldId);
    const identityCardNumberField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.identityCardNumberFieldId);
    const genderField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.genderFieldId);
    const birthDateField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.birthDateFieldId);
    const birthPlaceField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.birthPlaceFieldId);
    const nationalityField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.nationalityFieldId);
    const idCardNumberField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.idCardNumberFieldId);
    const idCardValidityField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.idCardValidityFieldId);
    const educationLevelField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.educationLevelFieldId);
    const educationInstitutionField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.educationInstitutionFieldId);
    const healthInsuranceCodeField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.healthInsuranceCodeFieldId);
    const maternityLeaveStartField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.maternityLeaveStartFieldId);
    const maternityLeaveEndField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.maternityLeaveEndFieldId);
    const parentalLeaveStartField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.parentalLeaveStartFieldId);
    const parentalLeaveEndField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.parentalLeaveEndFieldId);
    const invalidityField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.invalidityFieldId);
    const claimedChildrenField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.claimedChildrenFieldId);
    const unclaimedChildrenField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.unclaimedChildrenFieldId);
    const distrainmentField = getFirstFieldByFieldTypeIdRecursive(personCardRecord, FOND_JOB_FILE.distrainmentTypeFieldId);

    // Fond 88 (pracovní poměr)
    const employmentRecord = employeeDetail.employmentRecord;
    const personalNumberField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.personalNumberFieldId);
    const employmentStartDateField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.employmentStartDateFieldId);
    const trialPeriodEndDateField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.trialPeriodEndDateFieldId);
    const employmentEndDateField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.employmentEndDateFieldId);
    const workPositionField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.workPositionFieldId);
    const divisionField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.divisionFieldId);
    const bossField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.bossFieldId);
    const contractTypeField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.contractTypeFieldId);
    const contractDateField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.contractDateFieldId);
    const contractCategoryField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.contractCategoryFieldId);
    const employerField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.employerFieldId);
    const employerIcoField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.employerIcoFieldId);
    const typeOfTerminationField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.typeOfTerminationFieldId);
    const sideOfTerminationField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.sideOfTerminationFieldId);
    const reasonForTerminationField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.reasonForTerminationFieldId);
    const textNoteField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.textNoteFieldId);

    let textNoteValue = getTextFromGridFieldValue(textNoteField, '');

    const handleUpdateTextNote = (event: CustomEvent<string>) => {
        const response = service.editEmploymentStatusTextNote(employmentRecord, event.detail);

        if (!exists(response)) {
            return;
        }

        const editedTextNoteField = getFirstFieldByFieldTypeIdRecursive(employmentRecord, FOND_EMPLOYMENT_STATUS.textNoteFieldId);
        textNoteValue = getTextFromGridFieldValue(editedTextNoteField, '');
    };
</script>

<Flex direction="column" gap="6rem">
    <ErpFondPermissionContainer editableFondId="{FOND_JOB_FILE.fond}">
        <Flex direction="column">
            <Flex direction="row" gap="l" alignItems="center">
                <KpHeading type="h2">Výpis pracovní karty</KpHeading>

                <KpChipTag chipStyle="{employeeDetail.personCardActive ? 'success-new' : 'danger-new'}">
                    {employeeDetail.personCardActive ? 'Aktivní' : 'Neaktivní'}
                </KpChipTag>
            </Flex>

            <Spacer direction="vertical" size="xxl"/>

            {#if exists(personCardRecord)}
                <div class="user-detail-grid">
                    <LabeledValue label="Rodné číslo">
                        <GridFieldValue field="{birthNumberField}"/>
                    </LabeledValue>

                    <LabeledValue label="Číslo průkazky">
                        <GridFieldValue field="{identityCardNumberField}"/>
                    </LabeledValue>

                    <LabeledValue label="Pohlaví">
                        <GridFieldValue field="{genderField}"/>
                    </LabeledValue>

                    <LabeledValue label="Datum narození">
                        <GridFieldValue field="{birthDateField}"/>
                    </LabeledValue>

                    <LabeledValue label="Místo narození">
                        <GridFieldValue field="{birthPlaceField}"/>
                    </LabeledValue>

                    <LabeledValue label="Státní příslušnost">
                        <GridFieldValue field="{nationalityField}"/>
                    </LabeledValue>

                    <LabeledValue label="Číslo dokladu totoznosti">
                        <GridFieldValue field="{idCardNumberField}"/>
                    </LabeledValue>

                    <LabeledValue label="Platnost dokladu totoznosti">
                        <GridFieldValue field="{idCardValidityField}"/>
                    </LabeledValue>

                    <LabeledValue label="Vzdělání">
                        <GridFieldValue field="{educationLevelField}"/>
                    </LabeledValue>

                    <LabeledValue label="Instituce vzdělání">
                        <GridFieldValue field="{educationInstitutionField}"/>
                    </LabeledValue>

                    <LabeledValue label="Zdravotní pojišťovna">
                        <GridFieldValue field="{healthInsuranceCodeField}"/>
                    </LabeledValue>

                    <LabeledValue label="Mateřská dovolená od">
                        <GridFieldValue field="{maternityLeaveStartField}"/>
                    </LabeledValue>

                    <LabeledValue label="Mateřská dovolená do">
                        <GridFieldValue field="{maternityLeaveEndField}"/>
                    </LabeledValue>

                    <LabeledValue label="Rodičovská dovolená od">
                        <GridFieldValue field="{parentalLeaveStartField}"/>
                    </LabeledValue>

                    <LabeledValue label="Rodičovská dovolená do">
                        <GridFieldValue field="{parentalLeaveEndField}"/>
                    </LabeledValue>

                    <LabeledValue label="Invalidita">
                        <GridFieldValue field="{invalidityField}"/>
                    </LabeledValue>

                    <LabeledValue label="Uplatněné děti">
                        <GridFieldValue field="{claimedChildrenField}"/>
                    </LabeledValue>

                    <LabeledValue label="Neuplatněné děti">
                        <GridFieldValue field="{unclaimedChildrenField}"/>
                    </LabeledValue>

                    <LabeledValue label="Exekuce">
                        <GridFieldValue field="{distrainmentField}"/>
                    </LabeledValue>

                    <LabeledValue label="Pořadí pracovní karty">
                        <GridFieldValue value="{employeeDetail.personCardOrder}"/>
                    </LabeledValue>
                </div>
            {:else}
                <div class="no-record-container">
                    <IconedContent align="center" justify="center" orientation="vertical" icon="info" iconColor="var(--danger-red)">
                        Pracovník nemá žádnou pracovní kartu
                    </IconedContent>
                </div>
            {/if}
        </Flex>
    </ErpFondPermissionContainer>

    <ErpFondPermissionContainer editableFondId="{FOND_EMPLOYMENT_STATUS.fond}">
        <Flex direction="column">
            <Flex direction="row" gap="l" alignItems="center">
                <KpHeading type="h2">Výpis pracovního poměru</KpHeading>

                <KpChipTag chipStyle="{employeeDetail.employmentActive ? 'success-new' : 'danger-new'}">
                    {employeeDetail.employmentActive ? 'Aktivní' : 'Neaktivní'}
                </KpChipTag>
            </Flex>

            <Spacer direction="vertical" size="xxl"/>

            {#if exists(employmentRecord)}
                <div class="user-detail-grid">
                    <LabeledValue label="Osobní číslo">
                        <GridFieldValue field="{personalNumberField}"/>
                    </LabeledValue>

                    <LabeledValue label="Datum nástupu">
                        <GridFieldValue field="{employmentStartDateField}"/>
                    </LabeledValue>

                    <LabeledValue label="Zkušební doba do">
                        <GridFieldValue field="{trialPeriodEndDateField}"/>
                    </LabeledValue>

                    <LabeledValue label="Datum ukončení">
                        <GridFieldValue field="{employmentEndDateField}"/>
                    </LabeledValue>

                    <LabeledValue label="Pracovní pozice">
                        <GridFieldValue field="{workPositionField}"/>
                    </LabeledValue>

                    <LabeledValue label="Divize">
                        <GridFieldValue field="{divisionField}"/>
                    </LabeledValue>

                    <LabeledValue label="Nadřízený">
                        <GridFieldValue field="{bossField}"/>
                    </LabeledValue>

                    <LabeledValue label="Typ smlouvy">
                        <GridFieldValue field="{contractTypeField}"/>
                    </LabeledValue>

                    <LabeledValue label="Smlouva za dne">
                        <GridFieldValue field="{contractDateField}"/>
                    </LabeledValue>

                    <LabeledValue label="Kategorie smlouvy">
                        <GridFieldValue field="{contractCategoryField}"/>
                    </LabeledValue>

                    <LabeledValue label="Zaměstnavatel">
                        <GridFieldValue field="{employerField}"/>
                    </LabeledValue>

                    <LabeledValue label="IČO zaměstnavatele">
                        <GridFieldValue field="{employerIcoField}"/>
                    </LabeledValue>

                    <LabeledValue label="Typ výpovědi">
                        <GridFieldValue field="{typeOfTerminationField}"/>
                    </LabeledValue>

                    <LabeledValue label="Strana výpovědi">
                        <GridFieldValue field="{sideOfTerminationField}"/>
                    </LabeledValue>

                    <LabeledValue label="Důvod výpovědi">
                        <GridFieldValue field="{reasonForTerminationField}"/>
                    </LabeledValue>

                    <LabeledValue label="Pořadí pracovního poměru">
                        <GridFieldValue value="{employeeDetail.employmentOrder}"/>
                    </LabeledValue>

                    <TextNoteEditor textNote="{textNoteValue}" on:update-text-note={handleUpdateTextNote}/>
                </div>
            {:else}
                <div class="no-record-container">
                    <IconedContent align="center" justify="center" orientation="vertical" icon="info" iconColor="var(--danger-red)">
                        Pracovník nemá žádný pracovní poměr
                    </IconedContent>
                </div>
            {/if}
        </Flex>
    </ErpFondPermissionContainer>
</Flex>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";

    .user-detail-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: @spacing-l;
    }

    .no-record-container {
        display: flex;
        align-items: center;
        justify-content: center;
    }
</style>